if (!customElements.get("cart-drawer")) {
  class CartDrawerSection extends HTMLElement {
    cartUpdateUnsubscriber = undefined;

    constructor() {
      super();

      this.cartType = this.dataset.cartType;
      this.drawerClass = "wt-cart__drawer";
      this.pageOverlayClass = "page-overlay-cart";
      this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
      this.body = document.body;

      this.openTriggerQuery = [
        ".wt-cart__trigger",
        ".header__icon--cart",
        ".wt-cart__icon",
        "[data-cart-drawer]",
        ".cart-drawer-trigger"
      ].join(", ");

      this.isOpen = false;
      this.isCartPage = /\/cart(?:\/|$|\?)/.test(window.location.pathname);

      this.syncTimers = [];

      this.boundHandleKeydown = this.handleKeydown.bind(this);
      this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);
    }

    safe(fn) {
      try {
        return fn();
      } catch (e) {
        return null;
      }
    }

    connectedCallback() {
      if (this.cartType === "page" || this.isCartPage) return;

      this.rebindDynamicNodes();
      this.init();

      if (
        typeof subscribe === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined" &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          this.rebindDynamicNodes();

          if (this.isOpen || document.querySelector(".wt-cart__drawer--open")) {
            if (typeof setTabindex === "function") {
              setTabindex(this.toggleElements(), "0");
            }
            if (this.closeButton()) this.closeButton().focus();
          }

          this.scheduleKimoySync();
        });
      }

      this.scheduleKimoySync();
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = null;
      }

      this.clearSyncTimers();

      this.removeEventListener("keydown", this.boundHandleKeydown);
      document.removeEventListener("click", this.boundHandleDocumentClick, true);
    }

    clearSyncTimers() {
      if (!this.syncTimers || !this.syncTimers.length) return;

      this.syncTimers.forEach((timer) => {
        window.clearTimeout(timer);
      });

      this.syncTimers = [];
    }

    rebindDynamicNodes() {
      this.drawer = this.querySelector(`.${this.drawerClass}`);
      this.classDrawerActive = `${this.drawerClass}--open`;
      this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
      this.toggleElements = () =>
        this.querySelectorAll(this.dataset.toggleTabindex || "");
    }

    callKimoyRefresh() {
      const names = [
        "KimoyDrawerV120",
        "KimoyDrawerV119",
        "KimoyDrawerV118",
        "KimoyDrawerV117",
        "KimoyDrawerV116",
        "KimoyDrawerV115",
        "KimoyDrawerV114"
      ];

      names.forEach((name) => {
        this.safe(() => {
          if (window[name] && typeof window[name].refresh === "function") {
            window[name].refresh();
          }
        });
      });
    }

    scheduleKimoySync() {
      this.clearSyncTimers();

      [80, 260].forEach((delay) => {
        const timer = window.setTimeout(() => {
          this.callKimoyRefresh();
        }, delay);

        this.syncTimers.push(timer);
      });
    }

    getFocusableElements() {
      const selector = "button, [href], input, select, textarea, [tabindex]";
      const focusableElements = () =>
        Array.from(this.querySelectorAll(selector)).filter(
          (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0
        );

      return {
        focusableElements,
        first: focusableElements()[0],
        last: focusableElements()[focusableElements().length - 1]
      };
    }

    temporaryHideFocusVisible() {
      document.body.classList.add("no-focus-visible");
    }

    onToggle() {
      if (this.hasAttribute("open")) {
        this.removeAttribute("open");
        this.isOpen = false;
        this.temporaryHideFocusVisible();

        if (typeof setTabindex === "function") {
          setTabindex(this.toggleElements(), "-1");
        }

        this.body.classList.remove(this.activeOverlayBodyClass);
        if (this.drawer) this.drawer.classList.remove(this.classDrawerActive);
      } else {
        this.setAttribute("open", "");
        this.isOpen = true;
        this.temporaryHideFocusVisible();

        if (typeof setTabindex === "function") {
          setTabindex(this.toggleElements(), "0");
        }

        this.body.classList.add(this.activeOverlayBodyClass);
        if (this.drawer) this.drawer.classList.add(this.classDrawerActive);
        if (this.closeButton()) this.closeButton().focus();

        this.scheduleKimoySync();
      }
    }

    toggleDrawerClasses() {
      this.onToggle();
    }

    handleKeydown(e) {
      const isTabPressed = e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) this.toggleDrawerClasses();
      }

      if (isTabPressed && first && last) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    }

    handleDocumentClick(e) {
      const target = e.target;
      if (!target || !target.closest) return;

      if (
        target.closest(".wt-cart__show-cart") ||
        target.closest(".hero__button--link") ||
        target.closest('a[href="/cart"]') ||
        target.closest('a[href*="/cart"]') ||
        target.closest('[onclick*="/cart"]')
      ) {
        return;
      }

      if (target.closest(".wt-cart__drawer__close") && this.isOpen) {
        e.preventDefault();
        this.toggleDrawerClasses();
        return;
      }

      if (target.classList.contains(this.pageOverlayClass) && this.isOpen) {
        e.preventDefault();
        this.toggleDrawerClasses();
        return;
      }

      if (target.closest(this.openTriggerQuery) && !this.isOpen) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    }

    init() {
      this.removeEventListener("keydown", this.boundHandleKeydown);
      this.addEventListener("keydown", this.boundHandleKeydown);

      document.removeEventListener("click", this.boundHandleDocumentClick, true);
      document.addEventListener("click", this.boundHandleDocumentClick, true);
    }

    renderContents(parsedState, isClosedCart = true) {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (sectionElement && parsedState.sections[section.id]) {
          sectionElement.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.id],
            section.selector
          );
        }
      });

      this.rebindDynamicNodes();

      if (isClosedCart) {
        setTimeout(() => {
          this.toggleDrawerClasses();
          if (this.isOpen && this.closeButton()) this.closeButton().focus();
        }, 0);
      }

      this.scheduleKimoySync();
    }

    getSectionInnerHTML(html, selector = ".shopify-section") {
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const target = parsed.querySelector(selector);
      return target ? target.innerHTML : "";
    }

    getSectionsToRender() {
      return [
        {
          id: "cart-drawer",
          selector: "#CartDrawer"
        },
        {
          id: "cart-icon-bubble"
        }
      ];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define("cart-drawer", CartDrawerSection);
}

if (!customElements.get("cart-drawer-items")) {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: "CartDrawer",
          section: "cart-drawer",
          selector: ".drawer__inner"
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section"
        }
      ];
    }

    onCartUpdate() {
      Promise.all([
        fetch(`${routes.cart_url}?section_id=cart-drawer`).then((response) =>
          response.text()
        ),
        fetch(`${routes.cart_url}?section_id=cart-icon-bubble`).then((response) =>
          response.text()
        )
      ])
        .then(([drawerResponseText, bubbleResponseText]) => {
          const drawerHtml = new DOMParser().parseFromString(
            drawerResponseText,
            "text/html"
          );
          const bubbleHtml = new DOMParser().parseFromString(
            bubbleResponseText,
            "text/html"
          );

          const currentDrawerInner = document.querySelector(
            "#CartDrawer .drawer__inner"
          );
          const newDrawerInner =
            drawerHtml.querySelector("#CartDrawer .drawer__inner") ||
            drawerHtml.querySelector(".drawer__inner");

          if (currentDrawerInner && newDrawerInner) {
            currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
          }

          const bubbleTargetContainer = document.getElementById("cart-icon-bubble");
          const bubbleSource = bubbleHtml.querySelector(".shopify-section");

          if (bubbleTargetContainer && bubbleSource) {
            const bubbleTarget =
              bubbleTargetContainer.querySelector(".shopify-section") ||
              bubbleTargetContainer;
            bubbleTarget.innerHTML = bubbleSource.innerHTML;
          }

          this.captureCurrentServerQuantities();
          this.clearGlobalErrors();

          const drawerRoot = document.querySelector("cart-drawer");
          if (drawerRoot) {
            if (typeof drawerRoot.rebindDynamicNodes === "function") {
              drawerRoot.rebindDynamicNodes();
            }

            if (drawerRoot.isOpen || document.querySelector(".wt-cart__drawer--open")) {
              if (typeof setTabindex === "function") {
                setTabindex(drawerRoot.toggleElements(), "0");
              }
            }

            if (typeof drawerRoot.scheduleKimoySync === "function") {
              drawerRoot.scheduleKimoySync();
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  customElements.define("cart-drawer-items", CartDrawerItems);
}